import { useCallback, useEffect, useRef, useState } from "react";
import {
  BULK_PDF_EXPORT_CODE,
  getBulkPdfExportStatus,
  requestBulkPdfExport,
} from "../service/Task";

/** How often the status endpoint is polled while an export is queued or running. */
export const BULK_PDF_EXPORT_POLL_MS = 4000;

/** Consecutive poll failures tolerated before the UI gives up on an export. */
const MAX_POLL_FAILURES = 3;

export const BULK_PDF_EXPORT_PHASE = {
  IDLE: "idle",
  REQUESTING: "requesting",
  TRACKING: "tracking",
  COMPLETED: "completed",
  FAILED: "failed",
  LIMIT: "limit",
  EMPTY: "empty",
  ERROR: "error",
};

const IDLE_STATE = {
  phase: BULK_PDF_EXPORT_PHASE.IDLE,
  record: null,
  message: "",
};

const getBackendErrorMessage = (error) =>
  error?.response?.data?.body?.value ??
  error?.response?.data?.body?.error ??
  error?.response?.data?.message ??
  error?.body?.value ??
  error?.body?.error ??
  error?.message;

/**
 * Drives one bulk PDF export through request -> poll -> download (BLF-2171).
 *
 * The backend job outlives the page, so the poll loop is best-effort: it stops
 * on Completed/Failed, on unmount, and after a few consecutive network
 * failures -- the export itself keeps running and stays reachable from the
 * export-history screen either way.
 */
const useBulkPdfExport = () => {
  const [state, setState] = useState(IDLE_STATE);
  const timerRef = useRef(null);
  const isMountedRef = useRef(true);
  // Guards against a stale poll loop (an export the user has since replaced or
  // cancelled out of) writing over the current one's progress.
  const trackedExportIdRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const poll = useCallback(
    async (exportId, failureCount = 0) => {
      if (!isMountedRef.current || trackedExportIdRef.current !== exportId) return;

      let snapshot;
      try {
        const { code, value } = await getBulkPdfExportStatus(exportId);
        if (!isMountedRef.current || trackedExportIdRef.current !== exportId) return;

        if (code !== BULK_PDF_EXPORT_CODE.SUCCESS || !value) {
          setState({
            phase: BULK_PDF_EXPORT_PHASE.ERROR,
            record: null,
            message: typeof value === "string" ? value : "",
          });
          return;
        }
        snapshot = value;
      } catch (error) {
        if (!isMountedRef.current || trackedExportIdRef.current !== exportId) return;

        const nextFailureCount = failureCount + 1;
        if (nextFailureCount >= MAX_POLL_FAILURES) {
          setState((prev) => ({
            phase: BULK_PDF_EXPORT_PHASE.ERROR,
            record: prev.record,
            message: getBackendErrorMessage(error) || "",
          }));
          return;
        }
        timerRef.current = setTimeout(
          () => poll(exportId, nextFailureCount),
          BULK_PDF_EXPORT_POLL_MS
        );
        return;
      }

      const status = String(snapshot.status || "");
      if (status === "Completed") {
        setState({ phase: BULK_PDF_EXPORT_PHASE.COMPLETED, record: snapshot, message: "" });
        return;
      }
      if (status === "Failed") {
        setState({
          phase: BULK_PDF_EXPORT_PHASE.FAILED,
          record: snapshot,
          message: snapshot.errorMessage || "",
        });
        return;
      }

      setState({ phase: BULK_PDF_EXPORT_PHASE.TRACKING, record: snapshot, message: "" });
      timerRef.current = setTimeout(() => poll(exportId, 0), BULK_PDF_EXPORT_POLL_MS);
    },
    []
  );

  /** Poll an export that already exists (e.g. re-opened from the history screen). */
  const track = useCallback(
    (exportId, seedRecord = null) => {
      if (exportId == null) return;
      clearTimer();
      trackedExportIdRef.current = exportId;
      setState({
        phase: BULK_PDF_EXPORT_PHASE.TRACKING,
        record: seedRecord ?? { exportId, status: "Pending" },
        message: "",
      });
      poll(exportId, 0);
    },
    [clearTimer, poll]
  );

  /** Queue a new export, then start polling it. */
  const start = useCallback(
    async ({ workflowId, fromDate, toDate, accountNos }) => {
      clearTimer();
      trackedExportIdRef.current = null;
      setState({ phase: BULK_PDF_EXPORT_PHASE.REQUESTING, record: null, message: "" });

      let envelope;
      try {
        envelope = await requestBulkPdfExport({ workflowId, fromDate, toDate, accountNos });
      } catch (error) {
        if (!isMountedRef.current) return;
        setState({
          phase: BULK_PDF_EXPORT_PHASE.ERROR,
          record: null,
          message: getBackendErrorMessage(error) || "",
        });
        return;
      }

      if (!isMountedRef.current) return;

      const { code, value } = envelope;

      // 601/607 bodies are finished user-facing sentences -- surfaced as-is.
      if (code === BULK_PDF_EXPORT_CODE.LIMIT) {
        setState({
          phase: BULK_PDF_EXPORT_PHASE.LIMIT,
          record: null,
          message: typeof value === "string" ? value : envelope.message || "",
        });
        return;
      }
      if (code === BULK_PDF_EXPORT_CODE.NO_DATA) {
        setState({
          phase: BULK_PDF_EXPORT_PHASE.EMPTY,
          record: null,
          message: typeof value === "string" ? value : envelope.message || "",
        });
        return;
      }
      if (code !== BULK_PDF_EXPORT_CODE.SUCCESS || !value?.exportId) {
        setState({
          phase: BULK_PDF_EXPORT_PHASE.ERROR,
          record: null,
          message: typeof value === "string" ? value : envelope.message || "",
        });
        return;
      }

      trackedExportIdRef.current = value.exportId;
      setState({ phase: BULK_PDF_EXPORT_PHASE.TRACKING, record: value, message: "" });
      timerRef.current = setTimeout(() => poll(value.exportId, 0), BULK_PDF_EXPORT_POLL_MS);
    },
    [clearTimer, poll]
  );

  /** Stop polling and drop the tracked export (the backend job is unaffected). */
  const reset = useCallback(() => {
    clearTimer();
    trackedExportIdRef.current = null;
    setState(IDLE_STATE);
  }, [clearTimer]);

  const isBusy =
    state.phase === BULK_PDF_EXPORT_PHASE.REQUESTING ||
    state.phase === BULK_PDF_EXPORT_PHASE.TRACKING;

  return {
    phase: state.phase,
    record: state.record,
    message: state.message,
    isBusy,
    start,
    track,
    reset,
  };
};

export default useBulkPdfExport;
